// Nueva recepción — Taller → Operación → Nueva recepción (953).
// Recepción blindada: cliente (con cédula/RUC para la factura), equipo por número
// de serie (trae el historial y alerta de garantía o de cambio de dueño), falla,
// accesorios, estado físico, prueba de encendido delante del cliente, fotos,
// firma y consentimiento. Al guardar: ticket con QR del portal Pit Stop.
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { QRCodeSVG } from 'qrcode.react';
import { toast } from 'sonner';
import { AlertTriangle, Camera, Check, MessageCircle, Printer, Search, ShieldCheck, UserPlus, Wrench, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import { getErrorMessage } from '@/lib/api/client';
import { useTabsStore } from '@/stores/tabs.store';
import { comprimirFoto } from '@/features/hotel/hkOffline';
import { TicketOrden } from './OrdenesTaller';
import {
  Campo, fieldCls, tallerRpc, QK, urlPitStop, urlWhatsApp, fmtFecha, ESTADOS, TIPOS_EQUIPO, ACCESORIOS, CHECKS_FISICOS, PRUEBAS_ENCENDIDO,
  type Catalogo, type ConfigTaller, type EstadoOrden,
} from './shared';

interface Cliente { id: string; nombre: string; identificacion: string; celular: string | null; email: string | null; equipos: number; ordenes: number }
interface EquipoPrevio {
  id: string; tipo: string; marca: string | null; modelo: string | null; modelo_id: string | null; serial: string | null; color: string | null; partner_id: string; cliente: string;
  ordenes: { id: string; codigo: string; estado: EstadoOrden; fecha: string; falla: string; garantia_hasta: string | null }[] | null; garantia_vigente: boolean;
}
interface Creada { id: string; codigo: string; token: string; reingreso: boolean; otro_dueno: string | null }

const TRI = [[true, 'Sí'], [false, 'No'], [null, '—']] as const;

function Firma({ onChange }: { onChange: (dataUrl: string | null) => void }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const dibujando = useRef(false);
  const vacio = useRef(true);
  const pos = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    return [(e.clientX - r.left) * (e.currentTarget.width / r.width), (e.clientY - r.top) * (e.currentTarget.height / r.height)] as const;
  };
  const limpiar = () => { const c = ref.current; c?.getContext('2d')?.clearRect(0, 0, c.width, c.height); vacio.current = true; onChange(null); };
  return (
    <div>
      <canvas ref={ref} width={600} height={160} className="h-32 w-full touch-none rounded-md border bg-white"
        onPointerDown={(e) => { dibujando.current = true; const ctx = e.currentTarget.getContext('2d')!; const [x, y] = pos(e); ctx.lineWidth = 2.5; ctx.lineCap = 'round'; ctx.strokeStyle = '#111'; ctx.beginPath(); ctx.moveTo(x, y); e.currentTarget.setPointerCapture(e.pointerId); }}
        onPointerMove={(e) => { if (!dibujando.current) return; const ctx = e.currentTarget.getContext('2d')!; const [x, y] = pos(e); ctx.lineTo(x, y); ctx.stroke(); vacio.current = false; }}
        onPointerUp={(e) => { dibujando.current = false; if (!vacio.current) onChange(e.currentTarget.toDataURL('image/png')); }} />
      <button type="button" className="mt-1 text-xs text-muted-foreground underline" onClick={limpiar}>Borrar firma</button>
    </div>
  );
}

export function RecepcionTaller() {
  const qc = useQueryClient();
  const openTab = useTabsStore((s) => s.openTab);
  const { data: cat } = useQuery<Catalogo>({ queryKey: [QK, 'catalogo'], queryFn: () => tallerRpc('catalogo') });
  const { data: cfg } = useQuery<ConfigTaller>({ queryKey: [QK, 'config'], queryFn: () => tallerRpc('config_get') });

  // Cliente
  const [q, setQ] = useState('');
  const [cliente, setCliente] = useState<Cliente | null>(null);
  const [nuevoCli, setNuevoCli] = useState<{ nombre: string; identificacion: string; celular: string; email: string } | null>(null);
  const { data: clientes = [] } = useQuery<Cliente[]>({ queryKey: [QK, 'clientes', q], queryFn: () => tallerRpc('clientes_buscar', { _q: q }), enabled: !cliente && q.trim().length >= 2 });

  // Equipo
  const [serial, setSerial] = useState('');
  const [previo, setPrevio] = useState<EquipoPrevio | null>(null);
  const [eq, setEq] = useState({ tipo: 'consola', modelo_id: '', marca: '', modelo: '', color: '' });
  const { data: equiposCli = [] } = useQuery<EquipoPrevio[]>({ queryKey: [QK, 'equipos', cliente?.id], queryFn: () => tallerRpc('equipos_buscar', { _partner: cliente!.id }), enabled: !!cliente });
  const buscarSerial = async () => {
    if (!serial.trim()) return;
    try {
      const r = await tallerRpc<EquipoPrevio[]>('equipos_buscar', { _serial: serial.trim() });
      if (r[0]) { setPrevio(r[0]); toast.info(`Equipo conocido: ${r[0].ordenes?.length ?? 0} orden(es) previa(s)`); } else { setPrevio(null); toast('Equipo nuevo: complete los datos'); }
    } catch (e) { toast.error(getErrorMessage(e)); }
  };

  // Orden
  const [falla, setFalla] = useState('');
  const [acc, setAcc] = useState<string[]>([]);
  const [fisico, setFisico] = useState<Record<string, boolean | null>>({});
  const [notasFisico, setNotasFisico] = useState('');
  const [prueba, setPrueba] = useState<Record<string, boolean | null>>({});
  const [tecnico, setTecnico] = useState('');
  const [fecha, setFecha] = useState('');
  const [prioridad, setPrioridad] = useState<'normal' | 'urgente'>('normal');
  const [fotos, setFotos] = useState<{ datos: string; url: string }[]>([]);
  const [firma, setFirma] = useState<string | null>(null);
  const [consent, setConsent] = useState(false);
  const [creada, setCreada] = useState<Creada | null>(null);
  const [verTicket, setVerTicket] = useState(false);

  useEffect(() => { if (previo) setEq({ tipo: previo.tipo, modelo_id: previo.modelo_id ?? '', marca: previo.marca ?? '', modelo: previo.modelo ?? '', color: previo.color ?? '' }); }, [previo]);

  const crearCliente = useMutation({
    mutationFn: () => tallerRpc<{ id: string }>('cliente_crear', { _d: nuevoCli }),
    onSuccess: (r) => {
      setCliente({ id: r.id, nombre: nuevoCli!.nombre, identificacion: nuevoCli!.identificacion, celular: nuevoCli!.celular || null, email: nuevoCli!.email || null, equipos: 0, ordenes: 0 });
      setNuevoCli(null); toast.success('Cliente registrado');
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const guardar = useMutation({
    mutationFn: async () => {
      const r = await tallerRpc<Creada>('orden_crear', { _d: {
        partner_id: cliente!.id,
        equipo_id: previo?.id ?? null,
        equipo: previo ? null : { ...eq, serial: serial.trim() || null },
        falla_reportada: falla, accesorios: acc, estado_fisico: { ...fisico, notas: notasFisico || null }, prueba_encendido: prueba,
        tecnico_id: tecnico || null, fecha_prometida: fecha || null, prioridad, firma, consentimiento: consent,
      } });
      let fallo = 0;
      for (const f of fotos) {
        try { await tallerRpc('foto_subir', { _orden: r.id, _etapa: 'recibido', _datos: f.datos, _mime: 'image/jpeg', _descripcion: 'Recepción', _publica: true }); } catch { fallo++; }
      }
      if (fallo) toast.error(`${fallo} foto(s) no se pudieron subir: agréguelas desde la orden`);
      return r;
    },
    onSuccess: (r) => { setCreada(r); void qc.invalidateQueries({ queryKey: [QK] }); toast.success(`Orden ${r.codigo} creada`); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const agregarFotos = async (files: FileList | null) => {
    if (!files) return;
    for (const f of Array.from(files)) {
      try { const datos = await comprimirFoto(f); setFotos((x) => [...x, { datos, url: datos }]); } catch (e) { toast.error(getErrorMessage(e)); }
    }
  };
  const reiniciar = () => {
    setQ(''); setCliente(null); setSerial(''); setPrevio(null); setEq({ tipo: 'consola', modelo_id: '', marca: '', modelo: '', color: '' }); setFalla(''); setAcc([]); setFisico({});
    setNotasFisico(''); setPrueba({}); setTecnico(''); setFecha(''); setPrioridad('normal'); setFotos([]); setFirma(null); setConsent(false); setCreada(null); setVerTicket(false);
  };
  const abrirOrden = (id: string) => {
    try { sessionStorage.setItem('taller_abrir_orden', id); } catch { /* sin almacenamiento */ }
    openTab({ id: '/taller/ordenes', title: 'Órdenes', path: '/taller/ordenes' });
  };

  const equipoTxt = previo ? [previo.marca, previo.modelo].filter(Boolean).join(' ')
    : (eq.modelo_id ? (() => { const m = cat?.modelos.find((x) => x.id === eq.modelo_id); return m ? `${m.marca} ${m.modelo}${m.revision ? ` ${m.revision}` : ''}` : ''; })() : [eq.marca, eq.modelo].filter(Boolean).join(' '));

  if (creada && cliente) {
    const datosTicket = {
      codigo: creada.codigo, token: creada.token, created_at: new Date().toISOString(), falla_reportada: falla, accesorios: acc, fecha_prometida: fecha || null,
      cliente: { nombre: cliente.nombre, identificacion: cliente.identificacion, celular: cliente.celular },
      equipo: { marca: equipoTxt, modelo: null, serial: serial.trim() || previo?.serial || null },
    };
    if (verTicket) return <TicketOrden o={datosTicket} nombre={cfg?.config?.nombre_comercial ?? 'Taller'} terminos={cfg?.config?.terminos ?? null} onClose={() => setVerTicket(false)} />;
    const url = urlPitStop(creada.token);
    return (
      <div className="mx-auto max-w-xl space-y-4 p-6 text-center">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 text-emerald-700"><Check className="h-8 w-8" /></div>
        <h1 className="text-2xl font-semibold">Orden {creada.codigo} recibida</h1>
        {creada.reingreso && <div className="rounded-md bg-rose-50 p-2 text-sm text-rose-800">Reingreso en garantía: revise la orden anterior.</div>}
        {creada.otro_dueno && <div className="rounded-md bg-amber-50 p-2 text-sm text-amber-900">ATENCIÓN: este número de serie estaba registrado a nombre de {creada.otro_dueno}.</div>}
        <div className="mx-auto w-fit rounded bg-white p-3"><QRCodeSVG value={url} size={180} /></div>
        <p className="text-sm text-muted-foreground">El cliente escanea el QR y sigue la reparación en vivo: fotos, presupuesto para aprobar y pago del anticipo.</p>
        <div className="flex flex-wrap justify-center gap-2">
          <Button onClick={() => setVerTicket(true)}><Printer className="mr-1 h-4 w-4" /> Imprimir ticket y etiqueta</Button>
          <a href={urlWhatsApp(cliente.celular, `Hola ${cliente.nombre.split(' ')[0]}, recibimos tu ${equipoTxt} (orden ${creada.codigo}). Sigue la reparación aquí: ${url}`)} target="_blank" rel="noreferrer">
            <Button variant="outline"><MessageCircle className="mr-1 h-4 w-4" /> Enviar por WhatsApp</Button></a>
          <Button variant="outline" onClick={() => abrirOrden(creada.id)}><Wrench className="mr-1 h-4 w-4" /> Abrir la orden</Button>
          <Button variant="ghost" onClick={reiniciar}>Nueva recepción</Button>
        </div>
      </div>
    );
  }

  const listo = !!cliente && (!!previo || !!eq.tipo) && falla.trim().length >= 3 && consent;
  const modelos = (cat?.modelos ?? []).filter((m) => m.activo && m.tipo === eq.tipo);
  const tri = (val: boolean | null | undefined, set: (v: boolean | null) => void) => (
    <div className="flex gap-1">
      {TRI.map(([v, l]) => (
        <button key={l} type="button" onClick={() => set(v)}
          className={`rounded px-2 py-0.5 text-xs ${val === v || (v === null && val === undefined) ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>{l}</button>
      ))}
    </div>
  );

  return (
    <div className="space-y-3 p-4 sm:p-6">
      <h1 className="flex items-center gap-2 text-xl font-semibold"><Wrench className="h-5 w-5" /> Nueva recepción</h1>
      {!cfg?.config && <div className="rounded-md bg-amber-50 p-2 text-sm text-amber-900">Configure el taller primero (Taller → Configuración).</div>}

      <div className="grid gap-3 lg:grid-cols-2">
        {/* 1. Cliente */}
        <Card><CardContent className="space-y-2 p-4 text-sm">
          <div className="font-semibold">1. Cliente</div>
          {cliente ? (
            <div className="flex items-start justify-between rounded-md border bg-muted/30 p-2">
              <div><div className="font-medium">{cliente.nombre}</div><div className="text-xs text-muted-foreground">{cliente.identificacion} · {cliente.celular ?? 'sin celular'} · {cliente.ordenes} orden(es)</div></div>
              <button type="button" aria-label="Cambiar cliente" onClick={() => { setCliente(null); setPrevio(null); }}><X className="h-4 w-4" /></button>
            </div>
          ) : nuevoCli ? (
            <div className="grid gap-2 sm:grid-cols-2">
              <Campo label="Nombres y apellidos"><input className={fieldCls} value={nuevoCli.nombre} onChange={(e) => setNuevoCli({ ...nuevoCli, nombre: e.target.value })} /></Campo>
              <Campo label="Cédula / RUC / pasaporte"><input className={fieldCls} value={nuevoCli.identificacion} onChange={(e) => setNuevoCli({ ...nuevoCli, identificacion: e.target.value })} /></Campo>
              <Campo label="Celular (WhatsApp)"><input className={fieldCls} value={nuevoCli.celular} onChange={(e) => setNuevoCli({ ...nuevoCli, celular: e.target.value })} placeholder="09…" /></Campo>
              <Campo label="Correo (factura electrónica)"><input type="email" className={fieldCls} value={nuevoCli.email} onChange={(e) => setNuevoCli({ ...nuevoCli, email: e.target.value })} /></Campo>
              <div className="flex gap-2 sm:col-span-2">
                <Button size="sm" isLoading={crearCliente.isPending} onClick={() => crearCliente.mutate()}>Registrar cliente</Button>
                <Button size="sm" variant="ghost" onClick={() => setNuevoCli(null)}>Cancelar</Button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                  <input className={`${fieldCls} pl-7`} autoFocus placeholder="Nombre, cédula o celular" value={q} onChange={(e) => setQ(e.target.value)} />
                </div>
                <Button size="sm" variant="outline" onClick={() => setNuevoCli({ nombre: /\d/.test(q) ? '' : q, identificacion: /^\d+$/.test(q) ? q : '', celular: '', email: '' })}><UserPlus className="mr-1 h-4 w-4" /> Nuevo</Button>
              </div>
              <div className="max-h-48 space-y-1 overflow-y-auto">
                {clientes.map((c) => (
                  <button key={c.id} type="button" onClick={() => setCliente(c)} className="block w-full rounded border px-2 py-1 text-left hover:bg-muted">
                    <span className="font-medium">{c.nombre}</span> <span className="text-xs text-muted-foreground">{c.identificacion} · {c.celular ?? ''} · {c.equipos} equipo(s)</span>
                  </button>
                ))}
              </div>
            </>
          )}
          {cliente && equiposCli.length > 0 && !previo && (
            <div className="space-y-1">
              <div className="text-xs text-muted-foreground">Equipos que ya trajo:</div>
              {equiposCli.map((e) => (
                <button key={e.id} type="button" onClick={() => { setPrevio(e); setSerial(e.serial ?? ''); }} className="mr-1 rounded-full border px-2 py-0.5 text-xs hover:bg-muted">
                  {[e.marca, e.modelo].filter(Boolean).join(' ')} {e.serial ? `· ${e.serial}` : ''}{e.garantia_vigente ? ' · en garantía' : ''}
                </button>
              ))}
            </div>
          )}
        </CardContent></Card>

        {/* 2. Equipo */}
        <Card><CardContent className="space-y-2 p-4 text-sm">
          <div className="font-semibold">2. Equipo</div>
          <div className="flex gap-2">
            <input className={`${fieldCls} flex-1 font-mono uppercase`} placeholder="Número de serie (etiqueta inferior)" value={serial}
              onChange={(e) => { setSerial(e.target.value); if (previo) setPrevio(null); }} onKeyDown={(e) => { if (e.key === 'Enter') void buscarSerial(); }} />
            <Button size="sm" variant="outline" onClick={() => void buscarSerial()}><Search className="mr-1 h-4 w-4" /> Buscar</Button>
          </div>
          {previo && (
            <div className={`rounded-md border p-2 text-xs ${previo.garantia_vigente ? 'border-rose-300 bg-rose-50 text-rose-900 dark:bg-rose-950/30 dark:text-rose-200' : 'bg-muted/30'}`}>
              <div className="font-medium">{[previo.marca, previo.modelo].filter(Boolean).join(' ')} · de {previo.cliente}</div>
              {previo.garantia_vigente && <div className="flex items-center gap-1 font-semibold"><ShieldCheck className="h-3.5 w-3.5" /> Tiene garantía vigente: entrará como reingreso.</div>}
              {cliente && previo.partner_id !== cliente.id && <div className="flex items-center gap-1 font-semibold text-amber-700"><AlertTriangle className="h-3.5 w-3.5" /> Registrado a nombre de otra persona: verifique la procedencia.</div>}
              {(previo.ordenes ?? []).slice(0, 4).map((o) => <div key={o.id}>{o.codigo} · {fmtFecha(o.fecha)} · {ESTADOS[o.estado].label} · {o.falla}</div>)}
            </div>
          )}
          {!previo && (
            <div className="grid gap-2 sm:grid-cols-2">
              <Campo label="Tipo">
                <select className={fieldCls} value={eq.tipo} onChange={(e) => setEq({ ...eq, tipo: e.target.value, modelo_id: '' })}>
                  {TIPOS_EQUIPO.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </Campo>
              <Campo label="Modelo del catálogo">
                <select className={fieldCls} value={eq.modelo_id} onChange={(e) => setEq({ ...eq, modelo_id: e.target.value })}>
                  <option value="">— Otro (escribir) —</option>
                  {modelos.map((m) => <option key={m.id} value={m.id}>{m.marca} {m.modelo}{m.revision ? ` ${m.revision}` : ''}</option>)}
                </select>
              </Campo>
              {!eq.modelo_id && <>
                <Campo label="Marca"><input className={fieldCls} value={eq.marca} onChange={(e) => setEq({ ...eq, marca: e.target.value })} /></Campo>
                <Campo label="Modelo"><input className={fieldCls} value={eq.modelo} onChange={(e) => setEq({ ...eq, modelo: e.target.value })} /></Campo>
              </>}
              <Campo label="Color / señas"><input className={fieldCls} value={eq.color} onChange={(e) => setEq({ ...eq, color: e.target.value })} placeholder="blanco, sticker en la tapa…" /></Campo>
            </div>
          )}
        </CardContent></Card>
      </div>

      {/* 3. Falla y estado */}
      <Card><CardContent className="grid gap-4 p-4 text-sm lg:grid-cols-3">
        <div className="space-y-2">
          <div className="font-semibold">3. Falla reportada</div>
          <textarea className={`${fieldCls} h-24`} placeholder="Con las palabras del cliente: «se apaga a los 10 minutos de jugar»" value={falla} onChange={(e) => setFalla(e.target.value)} />
          <div className="text-xs font-semibold text-muted-foreground">Accesorios que deja</div>
          <div className="flex flex-wrap gap-1">
            {ACCESORIOS.map((a) => (
              <button key={a} type="button" onClick={() => setAcc(acc.includes(a) ? acc.filter((x) => x !== a) : [...acc, a])}
                className={`rounded-full px-2 py-0.5 text-xs ${acc.includes(a) ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>{a}</button>
            ))}
          </div>
        </div>
        <div className="space-y-1">
          <div className="font-semibold">Estado físico</div>
          {CHECKS_FISICOS.map(([k, l]) => <div key={k} className="flex items-center justify-between gap-2"><span>{l}</span>{tri(fisico[k], (v) => setFisico({ ...fisico, [k]: v }))}</div>)}
          <input className={fieldCls} placeholder="Detalle (p. ej. rayón esquina superior)" value={notasFisico} onChange={(e) => setNotasFisico(e.target.value)} />
        </div>
        <div className="space-y-1">
          <div className="font-semibold">Prueba de encendido (delante del cliente)</div>
          {PRUEBAS_ENCENDIDO.map(([k, l]) => <div key={k} className="flex items-center justify-between gap-2"><span>{l}</span>{tri(prueba[k], (v) => setPrueba({ ...prueba, [k]: v }))}</div>)}
        </div>
      </CardContent></Card>

      {/* 4. Asignación, fotos, firma */}
      <Card><CardContent className="grid gap-4 p-4 text-sm lg:grid-cols-3">
        <div className="space-y-2">
          <div className="font-semibold">4. Asignación</div>
          <Campo label="Técnico">
            <select className={fieldCls} value={tecnico} onChange={(e) => setTecnico(e.target.value)}>
              <option value="">— Asignar después —</option>
              {(cat?.tecnicos ?? []).filter((t) => t.activo).map((t) => <option key={t.id} value={t.id}>{t.nombre}</option>)}
            </select>
          </Campo>
          <Campo label="Fecha estimada de entrega"><input type="date" className={fieldCls} value={fecha} onChange={(e) => setFecha(e.target.value)} /></Campo>
          <label className="flex items-center gap-2"><input type="checkbox" checked={prioridad === 'urgente'} onChange={(e) => setPrioridad(e.target.checked ? 'urgente' : 'normal')} /> Urgente</label>
        </div>
        <div className="space-y-2">
          <div className="font-semibold">Fotos de recepción</div>
          <p className="text-xs text-muted-foreground">Las cuatro caras y el número de serie: protegen al taller ante «ya venía rayado».</p>
          <label className="inline-flex cursor-pointer items-center gap-1 rounded-md bg-primary px-3 py-2 text-sm text-primary-foreground">
            <Camera className="h-4 w-4" /> Tomar / subir fotos
            <input type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={(e) => void agregarFotos(e.target.files)} />
          </label>
          <div className="grid grid-cols-4 gap-1">
            {fotos.map((f, i) => (
              <div key={i} className="relative">
                <img src={f.url} alt={`Foto ${i + 1}`} className="h-16 w-full rounded object-cover" />
                <button type="button" aria-label="Quitar foto" className="absolute right-0 top-0 rounded-bl bg-black/60 text-white" onClick={() => setFotos(fotos.filter((_, j) => j !== i))}><X className="h-3 w-3" /></button>
              </div>
            ))}
          </div>
        </div>
        <div className="space-y-2">
          <div className="font-semibold">Firma del cliente</div>
          <Firma onChange={setFirma} />
        </div>
      </CardContent></Card>

      {/* 5. Consentimiento */}
      <Card><CardContent className="space-y-2 p-4 text-sm">
        <div className="font-semibold">5. Condiciones y consentimiento</div>
        {cfg?.config?.terminos && <div className="max-h-32 overflow-y-auto whitespace-pre-line rounded-md bg-muted/40 p-2 text-xs">{cfg.config.terminos}</div>}
        <p className="text-xs text-muted-foreground">
          Protección de datos (LOPDP): solo se registran los datos necesarios para la reparación y la factura. No pida ni anote contraseñas de cuentas
          (PSN, Xbox, Nintendo, Steam): si el técnico necesita entrar, el cliente las escribe en persona.
        </p>
        <label className="flex items-start gap-2"><input type="checkbox" className="mt-0.5" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
          El cliente acepta las condiciones del servicio, la garantía y el tratamiento de sus datos personales.</label>
      </CardContent></Card>

      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={reiniciar}>Limpiar</Button>
        <Button disabled={!listo} isLoading={guardar.isPending} onClick={() => guardar.mutate()}><Check className="mr-1 h-4 w-4" /> Recibir equipo</Button>
      </div>
    </div>
  );
}
